import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from '.'

export type ContactSortOrder = 'last_activity_desc' | 'name_asc' | 'created_desc'

export interface ContactIdentitySummary {
  platform: string
  count: number
  blocked_count: number
}

export interface ContactListItem {
  id: number
  name: string | null
  location: string | null
  notes_preview: string | null
  last_activity_at: string | null
  last_activity_at_iso_utc?: string | null
  last_activity_at_epoch?: number | null
  identities_summary: ContactIdentitySummary[]
  blocked: boolean
}

export interface ContactListResponse {
  items: ContactListItem[]
  cursor: string | null
}

export interface ListContactsParams {
  stationId: number
  q?: string
  platform?: string[]
  blocked?: 'true' | 'false'
  hasIdentities?: 'true' | 'false'
  sort?: ContactSortOrder
  cursor?: string | null
  limit?: number
}

const booleanParamMap: Record<'true' | 'false', string> = {
  true: 'true',
  false: 'false',
}

const buildListQuery = ({
  stationId,
  q,
  platform,
  blocked,
  hasIdentities,
  sort,
  cursor,
  limit,
}: ListContactsParams): string => {
  const params = new URLSearchParams()
  params.set('station_id', String(stationId))

  if (q) {
    params.set('q', q)
  }

  if (platform && platform.length > 0) {
    platform.forEach((value) => params.append('platform', value))
  }

  if (blocked) {
    params.set('blocked', booleanParamMap[blocked])
  }

  if (hasIdentities) {
    params.set('has_identities', booleanParamMap[hasIdentities])
  }

  if (sort) {
    params.set('sort', sort)
  }

  if (cursor) {
    params.set('cursor', cursor)
  }

  if (typeof limit === 'number') {
    params.set('limit', String(limit))
  }

  return params.toString()
}

export async function listContacts(params: ListContactsParams): Promise<ContactListResponse> {
  const query = buildListQuery(params)
  return apiGet<ContactListResponse>(`contacts?${query}`)
}

export interface ContactSummarySearchResult {
  id: number
  name: string | null
  location: string | null
}

export interface ContactSearchResponse {
  items: ContactSummarySearchResult[]
  next_cursor: string | null
}

export async function searchContacts(
  stationId: number,
  query: string,
  cursor?: string | null,
): Promise<ContactSearchResponse> {
  const params = new URLSearchParams()
  params.set('station_id', String(stationId))
  params.set('limit', '20')

  if (query) {
    params.set('q', query)
  }

  if (cursor) {
    params.set('cursor', cursor)
  }

  const response = await apiGet<ContactListResponse>(`contacts?${params.toString()}`)

  return {
    items: response.items.map((item) => ({
      id: item.id,
      name: item.name,
      location: item.location,
    })),
    next_cursor: response.cursor,
  }
}

export interface ContactDetail {
  id: number
  name: string | null
  location: string | null
  notes: string | null
  created_at: string
  last_activity_at: string | null
  last_activity_at_iso_utc?: string | null
  last_activity_at_epoch?: number | null
  blocked: boolean
}

export interface IdentityHeaderPreview {
  message_id?: string | null
  references_count?: number | null
}

export interface ContactIdentityDetail {
  id: number
  platform: string
  source: string
  source_label?: string | null
  external_id: string | null
  display_name: string | null
  first_seen_at: string | null
  last_seen_at: string | null
  message_count: number
  blocked: boolean
  header_preview?: IdentityHeaderPreview | null
}

export interface MergeHistoryItem {
  from_contact_id: number
  from_name: string | null
  merged_at: string
  merged_by: string
}

export interface ContactDetailResponse {
  contact: ContactDetail
  identities: ContactIdentityDetail[]
  merge_history: MergeHistoryItem[]
}

export async function getContactDetail(
  stationId: number,
  contactId: number,
): Promise<ContactDetailResponse> {
  const params = new URLSearchParams()
  params.set('station_id', String(stationId))
  return apiGet<ContactDetailResponse>(`contacts/${contactId}?${params.toString()}`)
}

export interface UpdateContactPayload {
  name?: string | null
  location?: string | null
  notes?: string | null
  block_contact?: boolean
  station_id: number
}

export async function updateContact(
  contactId: number,
  payload: UpdateContactPayload,
): Promise<ContactDetailResponse> {
  return apiPut<ContactDetailResponse>(`contacts/${contactId}`, payload)
}

export async function deleteContact(contactId: number, stationId: number): Promise<{ ok: boolean }> {
  const params = new URLSearchParams()
  params.set('station_id', String(stationId))
  return apiDelete<{ ok: boolean }>(`contacts/${contactId}?${params.toString()}`)
}

export interface ReassignIdentityToExistingPayload {
  station_id: number
  to_contact_id: number
}

export interface ReassignIdentityToNewPayload {
  station_id: number
  new_contact: {
    name?: string | null
    location?: string | null
    notes?: string | null
  }
}

export type ReassignIdentityPayload =
  | ReassignIdentityToExistingPayload
  | ReassignIdentityToNewPayload

export interface ReassignIdentityResponse {
  identity_id: number
  from_contact_id: number
  to_contact_id: number
}

export async function reassignIdentity(
  identityId: number,
  payload: ReassignIdentityPayload,
): Promise<ReassignIdentityResponse> {
  return apiPost<ReassignIdentityResponse>(`identities/${identityId}/reassign`, payload)
}

export async function blockIdentity(identityId: number, stationId: number): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>(`identities/${identityId}/block`, {
    station_id: stationId,
  })
}

export async function unblockIdentity(identityId: number, stationId: number): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>(`identities/${identityId}/unblock`, {
    station_id: stationId,
  })
}

export interface MergeContactsPayload {
  station_id: number
  from_contact_id: number
  to_contact_id: number
}

export interface MergeContactsResult {
  from_contact_id: number
  to_contact_id: number
  moved_identities: number
  moved_messages: number
}

export async function mergeContacts(payload: MergeContactsPayload): Promise<MergeContactsResult> {
  return apiPost<MergeContactsResult>('contacts/merge', payload)
}

export interface LinkIdentityResponse {
  ok: boolean
  identity_id: number
  new_contact_id: number
}

export async function linkIdentity(
  stationId: number,
  contactId: number,
  identityId: number,
): Promise<LinkIdentityResponse> {
  return apiPost<LinkIdentityResponse>(`contacts/${contactId}/link-identity`, {
    station_id: stationId,
    contact_id: contactId,
    identity_id: identityId,
  })
}
